'use client';

import { useQueryClient } from '@tanstack/react-query';
import { ExternalLink } from 'lucide-react';
import Link from 'next/link';
import { useEffect } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';

import { supabase } from '@/lib/supabase/client';
import { paths } from '@/constants/paths';
import { queryKeys } from '@/constants/query-keys';

import { ArticleRow } from '@/types/dao';

export const NotificationListener = () => {
  const queryClient = useQueryClient();
  useEffect(() => {
    const channel = supabase
      .channel('articles-notification-listener')
      .on<ArticleRow>(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'articles',
        },
        (payload) => {
          const oldStatus = payload.old.job_status;
          const newStatus = payload.new.job_status;
          const articleId = payload.new.id;
          const targetKeyword = payload.new.target_keyword;

          // Article generation status notifications
          if (oldStatus === 'in_progress' && newStatus === 'completed') {
            queryClient.invalidateQueries({
              queryKey: [queryKeys.articles],
            });
            toast.success('Article generated successfully', {
              description: `Keyword: "${targetKeyword}"`,
              action: (
                <Link
                  href={`${paths.contentAssistant.articles}/${articleId}`}
                  target='_blank'
                >
                  <Button variant='ghost' size='icon'>
                    <ExternalLink className='h-4 w-4' />
                  </Button>
                </Link>
              ),
            });
            // const audio = new Audio(`/audios/notification-sound.mp3`);
            // audio.play();
          }
          if (oldStatus !== 'failed' && newStatus === 'failed') {
            toast.error('Article generation failed', {
              description: `Keyword: "${targetKeyword}"`,
              action: (
                <Link
                  href={`${paths.contentAssistant.drafts}/${articleId}`}
                  target='_blank'
                >
                  <Button variant='ghost' size='icon'>
                    <ExternalLink className='h-4 w-4' />
                  </Button>
                </Link>
              ),
            });
          }

          // WordPress image upload notifications
          const oldImageStatus = payload.old.wordpress_image_status;
          const newImageStatus = payload.new.wordpress_image_status;

          if (oldImageStatus !== newImageStatus) {
            queryClient.invalidateQueries({
              queryKey: [queryKeys.articles],
            });

            if (newImageStatus === 'uploading') {
              toast.info('Uploading featured image to WordPress...', {
                description: `Keyword: "${targetKeyword}"`,
                duration: 3000,
              });
            } else if (newImageStatus === 'uploaded') {
              toast.success('Featured image uploaded!', {
                description: `Keyword: "${targetKeyword}"`,
                duration: 3000,
              });
            }
            // else if (newImageStatus === 'failed') {
            //   toast.error('Featured image upload failed', {
            //     description: `Keyword: "${targetKeyword}"`,
            //     duration: 5000,
            //   });
            // }
          }

          // WordPress article publishing notifications
          const oldPublishStatus = payload.old.wordpress_publishing_status;
          const newPublishStatus = payload.new.wordpress_publishing_status;
          const wordpressUrl = payload.new.wordpress_post_url;

          if (oldPublishStatus !== newPublishStatus) {
            queryClient.invalidateQueries({
              queryKey: [queryKeys.articles],
            });

            if (newPublishStatus === 'publishing') {
              toast.info('Publishing article to WordPress...', {
                description: `Keyword: "${targetKeyword}"`,
                duration: 3000,
              });
            } else if (newPublishStatus === 'published') {
              toast.success('Article published to WordPress!', {
                description: `Keyword: "${targetKeyword}"`,
                action: wordpressUrl ? (
                  <Link href={wordpressUrl} target='_blank'>
                    <Button variant='ghost' size='icon'>
                      <ExternalLink className='h-4 w-4' />
                    </Button>
                  </Link>
                ) : undefined,
              });
              // const audio = new Audio(`/audios/notification-sound.mp3`);
              // audio.play();
            }
            // else if (newPublishStatus === 'failed') {
            //   toast.error('WordPress publishing failed', {
            //     description: `Keyword: "${targetKeyword}"`,
            //     duration: 5000,
            //   });
            // }
          }
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  return null;
};
